<template>
  <div class="ui-panel wrp">
    <ui-state v-if="loading && !view" kind="loading" :tiles="0" :rows="4" />
    <ui-banner v-else-if="loadError" role="alert">
      {{ loadError }}
      <button type="button" class="ui-link" @click="load">{{ $t("supplier_po.retry_load") }}</button>
    </ui-banner>

    <template v-else-if="view">
      <!-- Poslati linkovi -->
      <ui-section v-if="view.dispatches.length" :title="$t('supplier_po.sent_links')">
        <ul class="wrp-links">
          <li v-for="d in view.dispatches" :key="d.id" class="wrp-link">
            <span class="wrp-link__icon"><v-icon size="16" color="currentColor">{{ channelIcon(d.channel) }}</v-icon></span>
            <span class="wrp-link__main">
              <b>{{ $t("supplier_po.link_n", { n: linkNo(d.id) }) }} · {{ dt(d.sent_at) }}</b>
              <small v-if="d.revoked_at" class="ui-err">{{ $t("supplier_po.revoked") }}</small>
              <small v-else-if="!d.active" class="ui-err">{{ $t("supplier_po.expired") }}</small>
              <small v-else>{{ $t("supplier_po.expires_at") }} {{ dt(d.expires_at, true) }}</small>
              <small v-if="d.recipients && d.recipients.length">{{ d.recipients.join(", ") }}</small>
            </span>
            <ui-chip :tone="d.first_opened_at ? 'ok' : 'plain'" :icon="d.first_opened_at ? 'mdi-eye-outline' : 'mdi-eye-off-outline'">
              {{ d.first_opened_at ? $t("supplier_po.opened_at") + " " + dt(d.first_opened_at) : $t("supplier_po.never_opened") }}
            </ui-chip>
            <div v-if="d.url || d.active" class="wrp-link__url">
              <input v-if="d.url" class="ui-input" readonly :value="maskUrl(d.url)" :aria-label="$t('supplier_po.sent_link')" />
              <small v-else class="ui-muted wrp-link__none">{{ $t("supplier_po.link_unavailable") }}</small>
              <button v-if="d.url" type="button" class="ui-btn ui-btn--sm" @click="copyLink(d)">
                <v-icon size="14" color="currentColor">{{ copiedId === d.id ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                {{ copiedId === d.id ? $t("supplier_po.copied") : $t("supplier_po.copy") }}
              </button>
              <button v-if="d.active" type="button" class="ui-btn ui-btn--sm ui-btn--danger" :disabled="!!busy" @click="revoke(d)">
                <v-icon size="14" color="currentColor">mdi-link-off</v-icon>
                {{ $t("supplier_po.revoke") }}
              </button>
            </div>
          </li>
        </ul>
        <div v-if="onResend" class="wrp-resend">
          <button type="button" class="ui-btn ui-btn--sm ui-btn--accent" :disabled="!!busy" data-action="send-again" @click="onResend">
            <v-icon size="14" color="currentColor">mdi-send</v-icon>
            {{ $t("supplier_po.send_again") }}
          </button>
        </div>
      </ui-section>

      <ui-banner v-if="!latest" tone="info">{{ $t("supplier_po.no_response_yet") }}</ui-banner>

      <template v-if="latest">
        <!-- Verzija odgovora -->
        <div class="wrp-head">
          <b>{{ $t("supplier_po.version", { n: latest.version }) }}</b>
          <ui-chip :tone="statusTone(latest.status)" pill>{{ $t("supplier_po.rs_" + latest.status.replace(/-/g, "_")) }}</ui-chip>
          <ui-chip v-if="latest.decision" :tone="decisionTone(latest.decision)" pill>{{ $t("supplier_po.decision_" + latest.decision) }}</ui-chip>
          <span class="ui-muted wrp-head__time">{{ dt(latest.created_at) }}</span>
        </div>

        <ui-kv v-if="metaRows.length" :rows="metaRows" />

        <ui-banner v-if="latest.status !== 'reject-supplier'" :tone="latest.changes_count ? 'warn' : 'info'">
          {{ latest.changes_count ? $t("supplier_po.changed_items", { n: latest.changes_count }) : $t("supplier_po.no_changes") }}
        </ui-banner>

        <!-- Traženo i ponuđeno jedno pored drugog -->
        <div class="ui-card">
          <div class="ui-table-wrap">
            <table class="ui-table ui-table--dense wrp-table">
              <thead>
                <tr>
                  <th>{{ $t("supplier_po.col_item") }}</th>
                  <th class="r">{{ $t("supplier_po.col_requested") }}</th>
                  <th class="r">{{ $t("supplier_po.col_offered") }}</th>
                  <th class="r">{{ $t("supplier_po.col_price") }}</th>
                  <th>{{ $t("supplier_po.col_availability") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="i in latest.items" :key="i.id" :class="{ 'wrp-changed': i.changed }">
                  <td>
                    <b>{{ i.name }}</b>
                    <small v-if="i.supplier_code">{{ $t("supplier_po.code") }}: {{ i.supplier_code }}</small>
                    <small v-if="i.substitute" class="ui-warn">{{ $t("supplier_po.substitute") }}: {{ i.substitute }}</small>
                    <small v-if="i.note">{{ i.note }}</small>
                  </td>
                  <td class="r nw ui-num">{{ num(i.requested_amount) }} {{ i.unit }}</td>
                  <td class="r nw ui-num" :class="{ 'wrp-diff': diffAmount(i) }">{{ num(i.offered_amount) }} {{ i.unit }}</td>
                  <td class="r nw ui-num" :class="{ 'wrp-diff': diffPrice(i) }">
                    <template v-if="i.unit_price !== null">{{ money(i.unit_price) }}</template>
                    <span v-else class="ui-muted">—</span>
                    <small v-if="diffPrice(i)">{{ $t("supplier_po.was") }} {{ money(i.requested_unit_price) }}</small>
                  </td>
                  <td><ui-chip :tone="availTone(i.availability)">{{ $t("supplier_po.av_" + i.availability) }}</ui-chip></td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td><b>{{ $t("supplier_po.total") }}</b></td>
                  <td class="r nw ui-num ui-muted" colspan="2">{{ $t("supplier_po.total_requested") }}: {{ money(latest.requested_total) }}</td>
                  <td class="r nw ui-num" colspan="2">
                    <small class="ui-muted wrp-offered">{{ $t("supplier_po.total_offered") }}</small>
                    <b>{{ money(latest.offered_total) }}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Odluka restorana -->
        <template v-if="canDecide">
          <div class="ui-field">
            <label for="wrp-note">{{ $t("supplier_po.decision_note") }}</label>
            <textarea id="wrp-note" v-model="note" class="wrp-textarea" rows="2" maxlength="1000"></textarea>
          </div>
          <ui-banner v-if="error" role="alert">{{ error }}</ui-banner>
          <div class="ui-form-actions wrp-actions">
            <button type="button" class="ui-btn ui-btn--danger" :disabled="!!busy" @click="decide('reject')">
              <v-icon size="16" color="currentColor">mdi-close</v-icon>{{ $t("supplier_po.reject") }}
            </button>
            <button type="button" class="ui-btn ui-btn--accent" :disabled="!!busy || !note.trim()" :title="note.trim() ? '' : $t('supplier_po.decision_note')" @click="decide('return')">
              <v-icon size="16" color="currentColor">mdi-undo-variant</v-icon>{{ $t("supplier_po.return") }}
            </button>
            <button type="button" class="ui-btn ui-btn--ok" :disabled="!!busy" @click="decide('accept')">
              <v-icon size="16" color="currentColor">mdi-check</v-icon>{{ $t("supplier_po.accept") }}
            </button>
          </div>
        </template>
        <ui-banner v-else-if="latest.decision" tone="info">
          <b>{{ $t("supplier_po.decision_" + latest.decision) }}</b> · {{ dt(latest.decided_at) }}
          <span v-if="latest.decision_note" class="wrp-pre"><br />{{ latest.decision_note }}</span>
        </ui-banner>
      </template>

      <!-- Ranije verzije i istorija -->
      <details v-if="olderResponses.length" class="wrp-more">
        <summary>{{ $t("supplier_po.history") }} ({{ olderResponses.length }})</summary>
        <ul>
          <li v-for="r in olderResponses" :key="r.id">
            <b>{{ $t("supplier_po.version", { n: r.version }) }}</b>
            · {{ $t("supplier_po.rs_" + r.status.replace(/-/g, "_")) }} · {{ dt(r.created_at) }}
            <span v-if="r.decision"> · {{ $t("supplier_po.decision_" + r.decision) }}</span>
            <span v-if="r.changes_count"> · {{ $t("supplier_po.changed_items", { n: r.changes_count }) }}</span>
            <small v-if="r.decision_note">{{ r.decision_note }}</small>
          </li>
        </ul>
      </details>
      <details v-if="view.events && view.events.length" class="wrp-more">
        <summary>{{ $t("supplier_po.events") }}</summary>
        <ul>
          <li v-for="e in view.events" :key="e.id">
            {{ dt(e.created_at) }} · {{ $t("supplier_po." + (e.payload && e.payload.already_exists ? "event_account_exists" : "event_" + e.type)) }}
            <span v-if="linkNo(e.dispatch_id)" class="wrp-tag">{{ $t("supplier_po.link_n", { n: linkNo(e.dispatch_id) }) }}</span>
          </li>
        </ul>
      </details>
    </template>
  </div>
</template>

<script>
import warehouseService from "@/services/warehouse";

/**
 * Odgovor dobavljača na poslatu narudžbenicu (panel novog magacina, na telefonu u donjem listu): traženo i ponuđeno jedno
 * pored drugog, razlike istaknute, odluka restorana (prihvati / odbij / vrati dobavljaču na doradu).
 * `onDone` se zove kad se nešto promijeni (odluka, opoziv linka) da se lista osvježi.
 */
export default {
  name: "WarehouseSupplierResponsePanel",
  inject: { shell: { from: "reportShell", default: null } },
  props: {
    document: { type: Object, required: true },
    onDone: { type: Function, default: null },
    /** Otvara panel za slanje (novi link); zadaje ga lista samo dok se narudžbenica još može slati. */
    onResend: { type: Function, default: null },
  },
  data: () => ({ view: null, loading: false, loadError: "", busy: "", note: "", error: "", copiedId: null }),
  computed: {
    locale() {
      const config = (this.$languageConfig && this.$languageConfig[this.$i18n.locale]) || {};
      return config.locale || "sr-RS";
    },
    latest() {
      return this.view && this.view.responses.length ? this.view.responses[0] : null;
    },
    /** Redni broj linka (1 = prvi poslat), da se događaji mogu povezati sa karticom linka. */
    linkNumbers() {
      const ids = (this.view ? this.view.dispatches : []).map((d) => d.id).sort((a, b) => a - b);
      return Object.fromEntries(ids.map((id, index) => [id, index + 1]));
    },
    olderResponses() {
      return this.view ? this.view.responses.slice(1) : [];
    },
    canDecide() {
      return !!(this.view && this.view.awaiting_decision && this.latest && !this.latest.decision);
    },
    metaRows() {
      const l = this.latest;
      if (!l) {
        return [];
      }
      const rows = [];
      if (l.delivery_date) rows.push({ key: "delivery", label: this.$t("supplier_po.delivery_date"), value: this.dt(l.delivery_date, true) });
      rows.push({ key: "vat", label: this.$t("supplier_po.vat_label"), value: l.prices_include_vat ? this.$t("supplier_po.vat_incl") : this.$t("supplier_po.vat_excl") });
      if (l.contact_name) rows.push({ key: "contact", label: this.$t("supplier_po.contact"), value: l.contact_name });
      if (l.note) rows.push({ key: "note", label: this.$t("supplier_po.supplier_note"), value: l.note });
      return rows;
    },
  },
  created() {
    this.load();
  },
  methods: {
    changed() {
      if (this.onDone) {
        this.onDone();
      }
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        this.view = (await warehouseService.getSupplierResponse(this.document.id)).data;
      } catch (e) {
        this.loadError = this.$t("supplier_po.error_load");
      } finally {
        this.loading = false;
      }
    },
    async decide(decision) {
      this.busy = decision;
      this.error = "";
      try {
        const { data } = await warehouseService.decideSupplierResponse(this.document.id, this.latest.id, {
          decision,
          note: this.note.trim() || null,
        });
        this.view = data;
        this.note = "";
        if (this.shell) {
          this.shell.showToast(this.$t("supplier_po.decision_saved"));
        }
        this.changed();
      } catch (e) {
        const res = e.response;
        const first = res && res.data && res.data.errors && Object.values(res.data.errors)[0];
        this.error = (first && first[0]) || this.$t("supplier_po.decision_error");
        this.load();
      } finally {
        this.busy = "";
      }
    },
    async revoke(d) {
      this.busy = "rev" + d.id;
      this.error = "";
      try {
        await warehouseService.revokeSupplierDispatch(this.document.id, d.id);
        await this.load();
        this.changed();
      } catch (e) {
        this.error = this.$t("supplier_po.decision_error");
      } finally {
        this.busy = "";
      }
    },
    /** Prikaz linka sa skrivenim tokenom: vidi se adresa i prvih 6 znakova, kopira se pun link. */
    maskUrl(url) {
      const cut = String(url).lastIndexOf("/") + 1;
      return `${url.slice(0, cut)}${url.slice(cut, cut + 6)}${"•".repeat(12)}`;
    },
    linkNo(id) {
      return id ? this.linkNumbers[id] || 0 : 0;
    },
    async copyLink(d) {
      try {
        await navigator.clipboard.writeText(d.url);
        this.copiedId = d.id;
        setTimeout(() => (this.copiedId = null), 2200);
      } catch (e) {
        // clipboard API nije dostupan (http / stari preglednik): kopiranje ne uspijeva
      }
    },
    channelIcon(c) {
      return { email: "mdi-email-outline", viber: "mdi-phone-message", whatsapp: "mdi-whatsapp", link: "mdi-link-variant" }[c] || "mdi-send";
    },
    statusTone(s) {
      return { confirm: "ok", "confirm-half": "warn", "reject-supplier": "bad" }[s] || "plain";
    },
    decisionTone(d) {
      return { accept: "ok", reject: "bad", return: "warn" }[d] || "plain";
    },
    availTone(a) {
      return { available: "ok", partial: "warn", unavailable: "bad" }[a] || "plain";
    },
    diffAmount(i) {
      return Math.abs(i.offered_amount - i.requested_amount) > 0.000001;
    },
    diffPrice(i) {
      return i.requested_unit_price > 0 && i.unit_price !== null && Math.abs(i.unit_price - i.requested_unit_price) > 0.00005;
    },
    num(n) {
      return Number(n).toLocaleString(this.locale, { maximumFractionDigits: 3 });
    },
    money(n) {
      return n === null || n === undefined ? "" : Number(n).toLocaleString(this.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    dt(v, dateOnly) {
      if (!v) {
        return "";
      }
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) {
        return v;
      }
      return dateOnly
        ? d.toLocaleDateString(this.locale)
        : `${d.toLocaleDateString(this.locale)} ${d.toLocaleTimeString(this.locale, { hour: "2-digit", minute: "2-digit" })}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.wrp {
  gap: 16px;
}

.wrp-pre {
  white-space: pre-wrap;
}

.wrp-tag {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--rm-chip-bg);
  color: var(--rm-chip-text);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.wrp-resend {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.wrp-links {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.wrp-link {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  grid-template-areas:
    "icon main chip"
    "url url url";
  gap: 10px;
  align-items: start;
  padding: 12px;
  border: 1px solid var(--rm-border);
  border-radius: 12px;
  background: var(--rm-surface);

  &__icon {
    grid-area: icon;
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--rm-chip-bg);
    color: var(--rm-chip-text);
  }

  &__main {
    grid-area: main;
    display: grid;
    gap: 1px;
    min-width: 0;

    small {
      color: var(--rm-muted);
      overflow-wrap: anywhere;
    }

    .ui-err {
      color: var(--rm-danger);
    }
  }

  .ui-chip {
    grid-area: chip;
    justify-self: end;
  }

  /* link i njegove akcije u jednom redu: polje, Kopiraj, Opozovi */
  &__url {
    grid-area: url;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    .ui-input {
      flex: 1 1 200px;
      min-width: 0;
      height: 32px;
      font-size: 13px;
    }

    .ui-btn {
      flex: none;
    }
  }

  &__none {
    flex: 1;
  }
}

/* uski ekran (donji list na telefonu): link u tri reda, dugmad odluke u mreži sa "Prihvati" preko cijele širine */
@media (max-width: 640px) {
  .wrp-link {
    grid-template-columns: 30px minmax(0, 1fr);
    grid-template-areas:
      "icon main"
      "chip chip"
      "url url";
  }

  .wrp-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;

    .ui-btn--ok {
      grid-column: 1 / -1;
      order: -1;
    }
  }
}

.wrp-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;

  b {
    font-size: 17px;
  }

  &__time {
    margin-left: auto;
    font-size: 13px;
  }
}

.wrp-table {
  td small {
    display: block;
    color: var(--rm-muted);
    font-size: 12px;
  }

  tr.wrp-changed {
    background: #fffbf2;

    td:first-child {
      box-shadow: inset 3px 0 0 #d97706;
    }
  }

  .wrp-diff {
    font-weight: 800;
    color: var(--rm-warn);
  }

  tfoot td {
    border-top: 2px solid var(--rm-border-strong);
    border-bottom: 0;
    background: #faf8f6;
  }

  .wrp-offered {
    margin-right: 8px;
  }
}

.wrp-textarea {
  width: 100%;
  min-height: 64px;
  padding: 10px 11px;
  border: 1px solid var(--rm-border-strong);
  border-radius: 10px;
  background: #fff;
  color: var(--rm-text);
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus-visible {
    outline: 2px solid var(--rm-accent);
    outline-offset: 1px;
  }
}

.wrp-actions {
  flex-wrap: wrap;
}

.wrp-more {
  border: 1px solid var(--rm-border);
  border-radius: 12px;
  background: var(--rm-surface);

  summary {
    padding: 11px 14px;
    font-weight: 700;
    cursor: pointer;
  }

  ul {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0 14px 12px;
    list-style: none;
    font-size: 13.5px;
  }

  small {
    display: block;
    color: var(--rm-muted);
  }
}
</style>
